import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import uniq from 'lodash-es/uniq';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    ActivityPurchasesNewOrderAddAutomaticArticlesOrderLinesAction,
    ActivityPurchasesNewOrderAddDepositAction,
    ActivityPurchasesNewOrderAddOrderLineAction,
    ActivityPurchasesNewOrderEditOrderLineAction,
    ActivityPurchasesNewOrderLoadRelatedListAction,
    ActivityPurchasesNewOrderRemoveDepositAction,
    ActivityPurchasesNewOrderRemoveOrderLineAction,
    ActivityPurchasesNewOrderResetSupplierAction,
    ActivityPurchasesNewOrderSaveAction,
    ActivityPurchasesNewOrderSetSupplierAndInitAction,
    ActivityPurchasesNewOrderUpdateOrderAction
} from 'src/app/board/activity/activity-shared/activity-suppliers-orders/stores/activity-purchases-new-order/activity-purchases-new-order.action';
import { AdministrativeStatus, MoyensPaiement } from 'src/app/board/activity/activity.constants';
import { arrayEmpty } from 'src/app/utils/array.utils';
import {
    AchatDevisCdeModelDto,
    AchatFournisseurInitModelDto,
    AchatLigneCdeModelDto,
    ArticleLightModel,
    ArticleModelDto,
    ComboBoxStatutsModel,
    FournisseurModelDto,
    MutationGetAchatFourInitModelArgs,
    MutationGetListeInfosLignesCommandeFournisseurArgs,
    MutationSaveDevisCdeFourArgs,
    OrganisationBaseModel,
    ReglementModelDto,
    SiteBaseModel,
    StatutFactureBaseModel
} from 'src/graphql/generated-types';

export type SupplierOrderLine = AchatLigneCdeModelDto & { PrixRemise: number; RemiseTotale: number; APayer: number };

export class ActivityPurchasesNewOrderModel {
    activityId: number = null;

    supplier: FournisseurModelDto = null;

    articlesList: ArticleModelDto[] = [];

    automaticArticlesList: ArticleLightModel[] = [];

    administrativeStatusList: ComboBoxStatutsModel[] = [];

    settlementStatusList: StatutFactureBaseModel[] = [];

    sitesList: SiteBaseModel[] = [];

    organizationsList: OrganisationBaseModel[] = [];

    order: AchatDevisCdeModelDto = null;

    depositsList: ReglementModelDto[] = [];

    checkIdsList: number[] = [];

    stockId: number = null;

    // Passe à true si l'objet à été mis à jour par l'utilisateur, auquel cas on ne regénère plus le label automatiquement
    isOrderLabelEditedByUser = false;
}

@State({
    defaults: new ActivityPurchasesNewOrderModel(),
    name: 'activityPurchaseNewOrder'
})
@Injectable()
export class ActivityPurchasesNewOrderState {

    constructor(private readonly graphqlService: GraphqlService) {
    }

    @Selector()
    public static activityId(state: ActivityPurchasesNewOrderModel): number {
        return state.activityId;
    }

    @Selector()
    public static supplier(state: ActivityPurchasesNewOrderModel): FournisseurModelDto {
        return state.supplier;
    }

    @Selector()
    public static articlesList(state: ActivityPurchasesNewOrderModel): ArticleModelDto[] {
        return state.articlesList;
    }

    @Selector()
    public static automaticArticlesList(state: ActivityPurchasesNewOrderModel): ArticleLightModel[] {
        return state.automaticArticlesList;
    }

    @Selector()
    public static administrativeStatusList(state: ActivityPurchasesNewOrderModel): ComboBoxStatutsModel[] {
        return state.administrativeStatusList;
    }

    @Selector()
    public static settlementStatusList(state: ActivityPurchasesNewOrderModel): StatutFactureBaseModel[] {
        return state.settlementStatusList;
    }

    @Selector()
    public static sitesList(state: ActivityPurchasesNewOrderModel): SiteBaseModel[] {
        return state.sitesList;
    }

    @Selector()
    public static organizationsList(state: ActivityPurchasesNewOrderModel): OrganisationBaseModel[] {
        return state.organizationsList;
    }

    @Selector()
    public static order(state: ActivityPurchasesNewOrderModel): AchatDevisCdeModelDto {
        return state.order;
    }

    @Selector()
    public static depositsList(state: ActivityPurchasesNewOrderModel): ReglementModelDto[] {
        return state.depositsList;
    }

    @Selector()
    public static checkIdsList(state: ActivityPurchasesNewOrderModel): number[] {
        return state.checkIdsList;
    }

    @Selector()
    public static orderLines(state: ActivityPurchasesNewOrderModel): SupplierOrderLine[] {
        return (state.order?.ListeLigneCdeModel?.AchatLigneCdeModelDTO || []).map(line => {
            const percentageSubvention = line.IsRemiseCumulable
                ? (line.RemiseUnitaireArticle + line.RemiseGlobalFour)
                : line.RemiseUnitaireArticle;

            const subventionAmout = (line.PrixAchatUnitaire * percentageSubvention);
            const unitAmount = (line.PrixAchatUnitaire - subventionAmout);

            return {
                ...line,
                RemiseUnitaireArticle: subventionAmout,
                PrixRemise: unitAmount,
                RemiseTotale: subventionAmout * line.QuantiteCmd,
                APayer: unitAmount * line.QuantiteCmd,
            };
        }) || [];
    }

    @Action(ActivityPurchasesNewOrderLoadRelatedListAction)
    loadRelatedLists({patchState}: StateContext<ActivityPurchasesNewOrderModel>, {activityId}: ActivityPurchasesNewOrderLoadRelatedListAction) {
        patchState({activityId});

        type NewOrderRelatedLists =
            'GetListeStatutAdmForFiltre'
            | 'GetListeStatutsFactureLight'
            | 'GetListeSiteBaseModel'
            | 'GetListeOrganisationBaseModel';
        return this.graphqlService.mutateMultiple<NewOrderRelatedLists>({
            mutation: gql`
                mutation NewOrderRelatedList(
                    $contexte: ContextInfosInput,
                    $criteresStatutAdmForFiltre: CriteresRechercheStatutsModelInput,
                    $criteresStatutFactureModel: CriteresStatutFactureModelInput,
                ) {
                    GetListeStatutAdmForFiltre(contexte: $contexte, criteres: $criteresStatutAdmForFiltre) {
                        ComboBoxStatutsModel {
                            CodeStatut
                            CodeStatutPere
                            IdStatut
                            IsStatutPerso
                            LibelleStatut
                        }
                    }
                    GetListeStatutsFactureLight(contexte: $contexte, criteres: $criteresStatutFactureModel) {
                        StatutFactureBaseModel {
                            IdStatutFacture
                            LibelleStatutFacture
                        }
                    }
                    GetListeSiteBaseModel(contexte: $contexte) {
                        SiteBaseModel {
                             IdSite
                             NomSite
                        }
                    }
                    GetListeOrganisationBaseModel(contexte: $contexte) {
                        OrganisationBaseModel {
                            IdOrganisation
                            NomOrganisation
                        }
                    }
                }
            `,
            variables: {
                criteresStatutAdmForFiltre: {
                    IsClos: false,
                    DisplayStatutsPeres: true,
                    DisplayStatutsFils: true,
                    ListeCodeStatuts: {string: [AdministrativeStatus.Invoiceable, AdministrativeStatus.Invoiced]},
                    IdActivite: activityId
                },
                criteresStatutFactureModel: {
                    IsTypeClient: true
                }
            }
        }).pipe(
            tap(({
                     GetListeStatutAdmForFiltre,
                     GetListeStatutsFactureLight,
                     GetListeSiteBaseModel,
                     GetListeOrganisationBaseModel,
                 }) => {
                patchState({
                    administrativeStatusList: GetListeStatutAdmForFiltre?.ComboBoxStatutsModel || [],
                    settlementStatusList: GetListeStatutsFactureLight?.StatutFactureBaseModel || [],
                    sitesList: GetListeSiteBaseModel?.SiteBaseModel || [],
                    organizationsList: GetListeOrganisationBaseModel?.OrganisationBaseModel || [],
                });
            })
        );
    }

    @Action(ActivityPurchasesNewOrderSetSupplierAndInitAction)
    initializeOrder({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {supplier}: ActivityPurchasesNewOrderSetSupplierAndInitAction) {
        patchState({supplier});

        return this.graphqlService.mutateOne<'GetAchatFourInitModel', MutationGetAchatFourInitModelArgs>({
            mutation: gql`
                mutation GetAchatFourInitModel($contexte: ContextInfosInput, $criteres: CritereAchatFourInitModelDTOInput) {
                    GetAchatFourInitModel(contexte: $contexte, criteres: $criteres) {
                        AchatDevisCdeModele {
                            DateCommande
                            ExerciceModel {
                                IdExercice
                            }
                            FraisCommissionFour
                            IdCE
                            IdCaisse
                            IdCommande
                            IdOrigineCde
                            IdPV
                            MontantFraiEnvoi
                            MontantRG
                            Objet
                            RemarquePrive
                            RemarquePublic
                            Status
                            TotalCde
                            TypeFonc
                        }
                        IdStock
                    }
                }
            `,
            variables: {
                criteres: {
                    WithFournisseur: false,
                    WithStockCE: true,
                    WithCommande: true,
                    // Identifiant de l'activité en cours
                    IdActivite: getState().activityId,
                }
            }
        }).pipe(
            tap(GetAchatFourInitModel => patchState({
                order: {
                    ...GetAchatFourInitModel?.AchatDevisCdeModele,
                    IdFournisseur: supplier.IdFournisseur,
                },
                stockId: GetAchatFourInitModel.IdStock
            })),
            switchMap(GetAchatFourInitModel => this.initSupplierData({
                getState,
                patchState
            }, GetAchatFourInitModel, supplier.IdFournisseur))
        );
    }

    @Action(ActivityPurchasesNewOrderAddOrderLineAction)
    addOrderLine({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {article, quantity}: ActivityPurchasesNewOrderAddOrderLineAction) {
        const orderLines = cloneDeep(getState().order?.ListeLigneCdeModel?.AchatLigneCdeModelDTO || []);

        const existingOrderLine = orderLines.find(orderLine =>
            orderLine.IdArticle === article.IdArticle
            && arrayEmpty(orderLine.ListeIdStkMvtReap?.decimal)
        );

        if (existingOrderLine) {
            existingOrderLine.QuantiteLivre += quantity;
            existingOrderLine.QuantiteCmd += quantity;

            patchState({
                order: {
                    ...getState().order,
                    ListeLigneCdeModel: {AchatLigneCdeModelDTO: orderLines}
                }
            });
        } else {
            orderLines.push({
                IdArticle: article.IdArticle,
                Libelle: `${article.LibelleProduit || ''}${article.LibelleProduit && article.LibelleArticle ? ' - ' : ''}${article.LibelleArticle || ''}`,
                LibelleArticle: article.LibelleArticle,
                LibelllePrd: article.LibelleProduit,
                LibelleFamille: article.LibellleFamille,
                PrixAchatUnitaire: article.Prix,
                RemiseUnitaireArticle: +article.RemiseUnitaireArticle,
                RemiseGlobalFour: article.RemiseGlobalFour,
                IsRemiseCumulable: article.IsRemiseCumulable,
                QuantiteLivre: quantity,
                QuantiteCmd: quantity,
            });

            const {Objet, ...order} = getState().order;

            patchState({
                order: {
                    ...order,
                    Objet: getState().isOrderLabelEditedByUser ? Objet : this.generateOrderLabel(orderLines),
                    ListeLigneCdeModel: {
                        AchatLigneCdeModelDTO: orderLines,
                    }
                }
            });
        }
    }

    @Action(ActivityPurchasesNewOrderRemoveOrderLineAction)
    removeOrderLine({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {orderLineIndex}: ActivityPurchasesNewOrderRemoveOrderLineAction) {
        if (orderLineIndex === -1) {
            return;
        }

        const orderLines = [...getState().order.ListeLigneCdeModel.AchatLigneCdeModelDTO];
        orderLines.splice(orderLineIndex, 1);
        const {Objet, ...order} = getState().order;

        patchState({
            order: {
                ...order,
                Objet: getState().isOrderLabelEditedByUser ? Objet : this.generateOrderLabel(orderLines),
                ListeLigneCdeModel: {
                    AchatLigneCdeModelDTO: orderLines
                }
            }
        });
    }

    @Action(ActivityPurchasesNewOrderEditOrderLineAction)
    editOrderLine({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {orderLine, orderLineIndex}: ActivityPurchasesNewOrderEditOrderLineAction) {
        const orderLines = cloneDeep(getState().order.ListeLigneCdeModel.AchatLigneCdeModelDTO);
        const updatedOrderLine = orderLines[orderLineIndex];
        if (!orderLine || !updatedOrderLine) {
            return;
        }

        const {RemarquePublic, RemarquePrive, QuantiteCmd, Libelle} = orderLine;
        Object.assign(updatedOrderLine, {RemarquePublic, RemarquePrive, QuantiteCmd, Libelle});

        patchState({
            order: {
                ...getState().order,
                ListeLigneCdeModel: {
                    AchatLigneCdeModelDTO: orderLines
                }
            }
        });
    }

    @Action(ActivityPurchasesNewOrderAddAutomaticArticlesOrderLinesAction)
    addAutomaticOrderLines({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {criterias}: ActivityPurchasesNewOrderAddAutomaticArticlesOrderLinesAction) {
        return this.graphqlService.mutateOne<'GetListeInfosLignesCommandeFournisseur', MutationGetListeInfosLignesCommandeFournisseurArgs>({
            mutation: gql`
                mutation GetListeInfosLignesCommandeFournisseur($contexte: ContextInfosInput, $critere: CriteresArticlesFournisseurModelInput) {
                    GetListeInfosLignesCommandeFournisseur(contexte: $contexte, critere: $critere) {
                        AchatLigneCdeModelDTO {
                            IdArticle
                            Libelle
                            LibelleArticle
                            LibelllePrd
                            LibelleFamille
                            ListeIdStkMvtReap {
                                decimal
                            }
                            IdLigneCmdClient
                            PrixAchatUnitaire
                            RemiseUnitaireArticle
                            RemiseGlobalFour
                            IsRemiseCumulable
                            IsCommandeReappro
                            QuantiteCmd
                            QuantiteLivre
                        }
                    }
                }
            `,
            variables: {
                critere: criterias
            }
        }).pipe(
            tap(GetListeInfosLignesCommandeFournisseur => {
                const orderLines = cloneDeep(getState().order?.ListeLigneCdeModel?.AchatLigneCdeModelDTO || []);
                const automaticOrderLines = GetListeInfosLignesCommandeFournisseur?.AchatLigneCdeModelDTO || [];

                const notInOrderLineIdStkMvtList = (orderList: number[], automaticOrderList: number[]): boolean => {
                    if (arrayEmpty(orderList) || arrayEmpty(automaticOrderList)) {
                        return false;
                    }
                    return automaticOrderList.filter(v => orderList.indexOf(v) === -1).length === 0;
                };

                automaticOrderLines.forEach((automaticOrderLine, i) => {
                    const existingOrderLine = orderLines.find(orderLine => orderLine.IdArticle === automaticOrderLine.IdArticle
                        && notInOrderLineIdStkMvtList(orderLine.ListeIdStkMvtReap?.decimal, automaticOrderLine.ListeIdStkMvtReap?.decimal)
                    );

                    const {Libelle, LibelllePrd, LibelleArticle} = automaticOrderLine;
                    if (!existingOrderLine
                        || (arrayEmpty(automaticOrderLine.ListeIdStkMvtReap?.decimal) && arrayEmpty(existingOrderLine.ListeIdStkMvtReap?.decimal))) {
                        orderLines.push({
                            ...automaticOrderLine,
                            Libelle: `${Libelle || [LibelllePrd, LibelleArticle].filter(e => !!e).join(' - ')}`
                        });
                    } else {
                        Object.assign(existingOrderLine, {
                            ...automaticOrderLine,
                            Libelle: existingOrderLine.Libelle || [LibelllePrd, LibelleArticle].filter(e => !!e).join(' - '),
                            RemarquePublic: existingOrderLine.RemarquePublic,
                            RemarquePrive: existingOrderLine.RemarquePrive
                        });
                    }
                });

                const {Objet, ...order} = getState().order;

                patchState({
                    order: {
                        ...order,
                        Objet: getState().isOrderLabelEditedByUser ? Objet : this.generateOrderLabel(orderLines),
                        ListeLigneCdeModel: {AchatLigneCdeModelDTO: orderLines}
                    }
                });
            })
        );
    }

    @Action(ActivityPurchasesNewOrderResetSupplierAction)
    resetSupplier({patchState}: StateContext<ActivityPurchasesNewOrderModel>) {
        patchState({
            order: null,
            articlesList: [],
            automaticArticlesList: [],
            depositsList: [],
            supplier: null,
            checkIdsList: [],
            isOrderLabelEditedByUser: false
        });
    }

    @Action(ActivityPurchasesNewOrderUpdateOrderAction)
    updateOrder({patchState, getState}: StateContext<ActivityPurchasesNewOrderModel>, {order}: ActivityPurchasesNewOrderUpdateOrderAction) {
        const isOrderLabelEditedByUser = getState().isOrderLabelEditedByUser || getState().order.Objet !== order.Objet;
        patchState({
            order,
            isOrderLabelEditedByUser
        });
    }

    @Action(ActivityPurchasesNewOrderAddDepositAction)
    addDeposit({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {payment}: ActivityPurchasesNewOrderAddDepositAction) {

        patchState({
            depositsList: [
                ...(getState().depositsList || []),
                payment,
            ]
        });
    }

    @Action(ActivityPurchasesNewOrderRemoveDepositAction)
    removeDeposit({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {index}: ActivityPurchasesNewOrderRemoveDepositAction) {
        patchState({
            depositsList: [
                ...getState().depositsList.filter((deposit, depositIndex) => index !== depositIndex) || [],
            ]
        });
    }

    @Action(ActivityPurchasesNewOrderSaveAction)
    saveOrder({getState, patchState}: StateContext<ActivityPurchasesNewOrderModel>, {amounts: {globalSubvention, shippingFees, supplierCommission}}: ActivityPurchasesNewOrderSaveAction) {

        const {activityId, order, depositsList, supplier, stockId} = getState();

        return this.graphqlService.mutateOne<'SaveDevisCdeFour', MutationSaveDevisCdeFourArgs>({
            mutation: gql`
                mutation SaveDevisCdeFour(
                    $contexte: ContextInfosInput,
                    $CdeFourFinalModel: AchatFournisseurFinalModelDTOInput,
                    $critere: CritereAchatFourFinalModelDTOInput,
                ) {
                    SaveDevisCdeFour(contexte: $contexte, CdeFourFinalModel: $CdeFourFinalModel,  critere: $critere) {
                        IdFacture
                        ListeEcriture {
                            CompteEcritureDTO {
                                Id
                                MoyenDePaiement {
                                    TypeMoyenPaiement {
                                        Code
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                critere: {},
                CdeFourFinalModel: {
                    TypeCommande: 'F',
                    IdActivite: activityId,
                    IdFournisseur: supplier.IdFournisseur,
                    IdCompteFraisCommission: supplier.IdCompteFraisComFour,
                    IdStock: stockId,
                    DateDevis: order.DateCommande,
                    FraisCommission: supplierCommission,
                    ListeAcompteModele: {ReglementModelDTO: depositsList},
                    AchatDevisCdeModele: {
                        ...order,
                        MontantFraiEnvoi: shippingFees,
                        MontantRG: globalSubvention,
                        FraisCommissionFour: supplierCommission,
                    },
                }
            }
        }).pipe(
            tap(SaveDevisCdeFour => {
                const checkPaymentsList = (SaveDevisCdeFour.ListeEcriture?.CompteEcritureDTO || []).filter(({MoyenDePaiement}) =>
                    MoyenDePaiement?.TypeMoyenPaiement?.Code === MoyensPaiement.ChequeBancaire
                );

                patchState({
                    checkIdsList: checkPaymentsList.map(({Id}) => Id)
                });
            })
        );
    }

    /**
     * Basé sur GenereLibelleCde de AchatDevisCdeModelDTO dans la G+
     */
    private generateOrderLabel(orderLines: AchatLigneCdeModelDto[]): string {
        if (arrayEmpty(orderLines)) {
            return '';
        }

        if (orderLines.length === 1) {
            const {LibelllePrd, LibelleArticle} = orderLines[0];
            return `${LibelllePrd || ''} - ${LibelleArticle || ''}`;
        }

        const productLabels = uniq(orderLines.map(({LibelllePrd}) => LibelllePrd));
        if (productLabels.length === 1) {
            return productLabels[0];
        }

        const familyLabel = uniq(orderLines.map(({LibelleFamille}) => LibelleFamille));
        if (familyLabel.length === 1) {
            return productLabels[0];
        }

        return 'Billetterie';
    }


    private initSupplierData({getState, patchState}, orderInitData: AchatFournisseurInitModelDto, supplierId: number): Observable<any> {
        type initSupplierArticles = 'InitInfosFournisseurSelectionne' | 'GetListeArticlesLight';

        return this.graphqlService.mutateMultiple<initSupplierArticles>({
            mutation: gql`
            mutation InitSupplierData(
                $contexte: ContextInfosInput,
                $criteresArticleDTOInput: CriteresArticleDTOInput,
                $criteresArticleLightModel: CriteresArticleLightModelInput
            ) {
                InitInfosFournisseurSelectionne(contexte: $contexte critere: $criteresArticleDTOInput) {
                    ListeArticleModele {
                        ArticleModelDTO {
                            IdArticle
                            LibelleArticle
                            LibelleProduit
                            LibellleFamille
                            Prix
                            IsRemiseCumulable
                            RemiseUnitaireArticle
                            RemiseGlobalFour
                            IsGenerique
                            IsGestionStock
                            IsArticleReappo
                            QuantiteDisponible
                            Picto {
                                PictoFullUrl
                            }
                        }
                    }
                }
                GetListeArticlesLight(contexte: $contexte Criteres: $criteresArticleLightModel) {
                    ArticleLightModel {
                        IdArticle
                        Libelle
                    }
                }
            }`,
            variables: {
                criteresArticleDTOInput: {
                    IsCloture: false,
                    IsGenerique: false,
                    IsFiltreIdFour: true,
                    WithProduit: true,
                    WithArtFour: true,
                    IsNotNullablePrix: true,
                    IdFournisseur: supplierId,
                    IdStock: orderInitData.IdStock,
                    DateArticleAchatFour: orderInitData.AchatDevisCdeModele.DateCommande,
                    IdActivite: getState().activityId,
                },
                criteresArticleLightModel: {
                    IsClos: false,
                    IdActivite: getState().activityId,
                    IdFournisseur: supplierId,
                }
            }
        }).pipe(
            tap(({InitInfosFournisseurSelectionne, GetListeArticlesLight}) => {
                patchState({
                    articlesList: InitInfosFournisseurSelectionne.ListeArticleModele?.ArticleModelDTO || [],
                    automaticArticlesList: GetListeArticlesLight?.ArticleLightModel || []
                });
            })
        );
    }
}
